import React, { useState, useMemo, useContext, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../../../components/general/GlobalStateProvider';
import DataOps from '../../../../data/DataOps';
import RegistrationBanner from '../../../../components/session/RegistrationBanner';
import SessionTypeBanner from '../../../../components/session/SessionTypeBanner';
import SessionTypeEditModal from './SessionTypeEditModal';
import ClassSizeEditModal from './ClassSizeEditModal';
import PriceEditModal from './PriceEditModal';
import TitleEditModal from './TitleEditModal';
import AgeRangeEditModal from './AgeRangeEditModal';
import TimeRangeEditModal from './TimeRangeEditModal';
import DateRangeEditModal from './DateRangeEditModal';
import TimeOps from '../../../../data/TimeOps';
import SessionUtils from '../../../../utility/SessionUtils';
import CoachViewModal from '../../../../components/session/CoachViewModal';
import CoachEditModal from './CoachEditModal';
import CancellationPolicyEditModal from './CancellationPolicyEditModal';
import NotificationOps from '../../../../data/NotificationOps';

const SessionsTableView = ({ sessions, isEditing, setSessions, addSession, isHost = false, urlViewType = null, urlDate = null, sessionIdFromUrl = null, shouldOpenCamperView = false, camp = null }) => {
    const { user } = useContext(GlobalStateContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortColumn, setSortColumn] = useState('title');
    const [sortDirection, setSortDirection] = useState('asc');
    const [coachData, setCoachData] = useState<any[]>([]);
    const [showCoachViewModal, setShowCoachViewModal] = useState(false);
    const [coachViewSessionId, setCoachViewSessionId] = useState<string | null>(null);
    const [coachViewCampId, setCoachViewCampId] = useState<string | null>(null);

    // Fetch coach data when camp changes
    useEffect(() => {
        const fetchCoachData = async () => {
            if (!camp?.campCoaches || camp.campCoaches.length === 0) {
                setCoachData([]);
                return;
            }
            const coaches = await DataOps.fetchCoachDataList(camp.campCoaches);
            setCoachData(coaches);
        };

        fetchCoachData();
    }, [camp?.campCoaches]);

    // Open coach view modal when navigating from notification
    useEffect(() => {
        if (shouldOpenCamperView && sessionIdFromUrl && camp?.campId) {
            setCoachViewSessionId(sessionIdFromUrl);
            setCoachViewCampId(camp.campId);
            setShowCoachViewModal(true);
        }
    }, [shouldOpenCamperView, sessionIdFromUrl, camp?.campId]);


    // State for tracking which edit modal is open
    const [editingModalSessionId, setEditingModalSessionId] = useState(null);
    const [modalType, setModalType] = useState(null); // 'classSize', 'price', 'title', etc.
    const [hoveredCell, setHoveredCell] = useState(null);
    const [showInstructorModal, setShowInstructorModal] = useState(false);

    const handleDeleteSession = (sessionId: string) => {
        if (window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
            setSessions(prev => {
                const updated = { ...prev };
                delete updated[sessionId];
                return updated;
            });
        }
    };

    // Use TimeOps for date formatting
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        return timeString;
    };

    const parseTimeForSorting = (timeString) => {
        if (!timeString) return 0;

        // Convert time to minutes for sorting
        const time = timeString.toLowerCase();
        let hours = 0;
        let minutes = 0;

        if (time.includes('am') || time.includes('pm')) {
            // Handle 12-hour format
            const match = time.match(/(\d+):?(\d*)\s*(am|pm)/);
            if (match) {
                hours = parseInt(match[1]);
                minutes = match[2] ? parseInt(match[2]) : 0;
                const period = match[3];

                if (period === 'pm' && hours !== 12) hours += 12;
                if (period === 'am' && hours === 12) hours = 0;
            }
        } else {
            // Handle 24-hour format
            const match = time.match(/(\d+):(\d+)/);
            if (match) {
                hours = parseInt(match[1]);
                minutes = parseInt(match[2]);
            }
        }

        return hours * 60 + minutes;
    };

    const handleCalendarPress = (session) => {
        if (!session.startDate || !session.endDate || !session.startTime || !session.endTime) return;

        // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
        const formatDateTimeForCalendar = (date, time) => {
            const dateObj = new Date(`${date} ${time}`);
            return dateObj.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const startDateTime = formatDateTimeForCalendar(session.startDate, session.startTime);
        const endDateTime = formatDateTimeForCalendar(session.endDate, session.endTime);

        // Create event details
        const title = encodeURIComponent(session.title || 'Camp Session');
        const location = encodeURIComponent(session.campAddress || '');
        const details = encodeURIComponent(`Camp session: ${session.title || 'Untitled'}`);

        // Create Google Calendar URL
        const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateTime}/${endDateTime}&location=${location}&details=${details}`;

        window.open(calendarUrl, '_blank');
    };

    const handleCoachEmailPress = (session) => {
        if (!session.coach?.email) return;
        const coachName = `${session.coach.firstName || ''} ${session.coach.lastName || ''}`.trim() || 'Coach';
        const subject = encodeURIComponent(`Inquiry about ${session.title || 'Camp Session'}`);
        const body = encodeURIComponent(`Hello ${coachName},\n\nI'm interested in learning more about your camp session "${session.title || 'your camp session'}" scheduled for ${session.startDate} - ${session.endDate}.\n\nThank you!`);
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${session.coach.email}&su=${subject}&body=${body}`;
        window.open(gmailUrl, '_blank');
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${price}`;
    };

    const formatCapacity = (capacity) => {
        if (!capacity) return 'N/A';
        return `${capacity}`;
    };

    const formatAgeRange = (startAge, endAge) => {
        if (!startAge || !endAge) return 'N/A';
        return `${startAge} - ${endAge}`;
    };

    // Remove the calculateDaysDuration function since it's now in TimeOps
    const calculateDaysDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return '';

        try {
            let start, end;

            // Handle different date formats
            if (typeof startDate === 'string') {
                if (startDate.includes('/')) {
                    // MM/DD/YYYY format
                    const parts = startDate.split('/');
                    start = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
                } else if (startDate.includes('th') || startDate.includes('st') || startDate.includes('nd') || startDate.includes('rd')) {
                    // "Jan 15th" format
                    const dateMatch = startDate.match(/(\w+)\s+(\d+)/);
                    if (dateMatch) {
                        const month = dateMatch[1];
                        const day = parseInt(dateMatch[2]);
                        const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
                        if (monthIndex !== -1) {
                            start = new Date(new Date().getFullYear(), monthIndex, day);
                        }
                    }
                } else {
                    start = new Date(startDate);
                }
            } else {
                start = new Date(startDate);
            }

            if (typeof endDate === 'string') {
                if (endDate.includes('/')) {
                    // MM/DD/YYYY format
                    const parts = endDate.split('/');
                    end = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
                } else if (endDate.includes('th') || endDate.includes('st') || endDate.includes('nd') || endDate.includes('rd')) {
                    // "Jan 15th" format
                    const dateMatch = endDate.match(/(\w+)\s+(\d+)/);
                    if (dateMatch) {
                        const month = dateMatch[1];
                        const day = parseInt(dateMatch[2]);
                        const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
                        if (monthIndex !== -1) {
                            end = new Date(new Date().getFullYear(), monthIndex, day);
                        }
                    }
                } else {
                    end = new Date(endDate);
                }
            } else {
                end = new Date(endDate);
            }

            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                return '';
            }

            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days

            return `(${diffDays} day${diffDays === 1 ? '' : 's'})`;
        } catch (error) {
            console.error('Error calculating days duration:', error, startDate, endDate);
            return '';
        }
    };

    const calculateHoursDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return '';

        try {
            const startMinutes = parseTimeForSorting(startTime);
            const endMinutes = parseTimeForSorting(endTime);

            if (startMinutes === 0 && endMinutes === 0) return '';

            let duration = endMinutes - startMinutes;
            if (duration < 0) duration += 24 * 60; // Handle overnight sessions

            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;

            if (hours === 0 && minutes === 0) return '';

            if (minutes === 0) {
                return `(${hours} hour${hours === 1 ? '' : 's'})`;
            } else {
                return `(${hours}h ${minutes}m)`;
            }
        } catch (error) {
            return '';
        }
    };

    const formatSessionType = (sessionType) => {
        if (!sessionType) return 'N/A';
        return sessionType.replace('_', ' ').replace('-', ' ');
    };

    const formatRegistrationStatus = (status) => {
        if (!status) return 'N/A';
        return status.replace('_', ' ');
    };

    // Helper functions for RegistrationBanner
    const isUserRegistered = (sessionId) => {
        if (user && user.upcomingSessions) {
            return user.upcomingSessions.includes(sessionId);
        }
        return false;
    };





    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn !== column) return null;
        return (
            <MaterialCommunityIcons
                name={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
                size={16}
                color={'#2C67D6'}
                style={{ marginLeft: 4 }}
            />
        );
    };

    const filteredAndSortedSessions = useMemo(() => {
        let filtered = Object.entries(sessions).filter(([sessionId, session]: [string, any]) => {
            const searchLower = searchTerm.toLowerCase();
            return (
                (session.title || '').toLowerCase().includes(searchLower) ||
                (session.sessionType || '').toLowerCase().includes(searchLower) ||
                (session.startDate || '').toLowerCase().includes(searchLower) ||
                (session.price || '').toString().includes(searchLower) ||
                (session.registrationStatus || '').toLowerCase().includes(searchLower)
            );
        });

        filtered.sort(([idA, sessionA]: [string, any], [idB, sessionB]: [string, any]) => {
            let a, b;

            switch (sortColumn) {
                case 'title':
                    a = sessionA.title || '';
                    b = sessionB.title || '';
                    break;
                case 'dates':
                    // For formatted strings like "Jan 15th", extract month and day for sorting
                    const getDateForSorting = (dateStr) => {
                        if (!dateStr) return 0;
                        if (typeof dateStr === 'string' && (dateStr.includes('th') || dateStr.includes('st') || dateStr.includes('nd') || dateStr.includes('rd'))) {
                            // Extract month and day from "Jan 15th" format
                            const match = dateStr.match(/(\w+)\s+(\d+)/);
                            if (match) {
                                const month = match[1];
                                const day = parseInt(match[2]);
                                const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(month);
                                return monthIndex * 100 + day;
                            }
                        }
                        // Fallback to timestamp for other formats
                        return new Date(dateStr || '').getTime();
                    };
                    a = getDateForSorting(sessionA.startDate);
                    b = getDateForSorting(sessionB.startDate);
                    break;
                case 'time':
                    a = parseTimeForSorting(sessionA.startTime);
                    b = parseTimeForSorting(sessionB.startTime);
                    break;
                case 'price':
                    a = parseFloat(sessionA.price) || 0;
                    b = parseFloat(sessionB.price) || 0;
                    break;
                case 'capacity':
                    a = parseInt(sessionA.capacity) || 0;
                    b = parseInt(sessionB.capacity) || 0;
                    break;
                case 'ageRange':
                    a = parseInt(sessionA.startAge) || 0;
                    b = parseInt(sessionB.startAge) || 0;
                    break;
                case 'coach':
                    a = `${sessionA.coach?.firstName || ''} ${sessionA.coach?.lastName || ''}`.trim();
                    b = `${sessionB.coach?.firstName || ''} ${sessionB.coach?.lastName || ''}`.trim();
                    break;
                case 'sessionType':
                    a = sessionA.sessionType || '';
                    b = sessionB.sessionType || '';
                    break;

                case 'registrationStatus':
                    a = sessionA.registrationStatus || '';
                    b = sessionB.registrationStatus || '';
                    break;
                default:
                    return 0;
            }

            if (sortDirection === 'asc') {
                return a > b ? 1 : -1;
            } else {
                return a < b ? 1 : -1;
            }
        });

        return filtered;
    }, [sessions, searchTerm, sortColumn, sortDirection]);

    return (
        <View style={styles.container}>
            {/* Search Bar */}
            <View style={styles.searchContainer}>
                <MaterialCommunityIcons name="magnify" size={20} color="#666" style={styles.searchIcon} />
                <TextInput
                    style={styles.searchInput}
                    placeholder="Search sessions..."
                    value={searchTerm}
                    onChangeText={setSearchTerm}
                    placeholderTextColor="#999"
                />
            </View>

            {/* Table Card */}
            <View style={styles.tableCard}>
                {/* Table Header */}
                <View style={styles.headerRow}>
                    <Pressable style={styles.headerCell} onPress={() => handleSort('title')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Session</Text>{getSortIcon('title')}</View>
                    </Pressable>
                    <Pressable style={styles.dateHeaderCell} onPress={() => handleSort('dates')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Dates</Text>{getSortIcon('dates')}</View>
                    </Pressable>
                    <Pressable style={styles.headerCell} onPress={() => handleSort('time')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Time</Text>{getSortIcon('time')}</View>
                    </Pressable>
                    <Pressable style={styles.compactHeaderCell} onPress={() => handleSort('price')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Price</Text>{getSortIcon('price')}</View>
                    </Pressable>
                    <Pressable style={styles.extraCompactHeaderCell} onPress={() => handleSort('capacity')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Class Size</Text>{getSortIcon('capacity')}</View>
                    </Pressable>
                    <Pressable style={styles.compactHeaderCell} onPress={() => handleSort('ageRange')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Age Group</Text>{getSortIcon('ageRange')}</View>
                    </Pressable>
                    <Pressable style={styles.headerCell} onPress={() => handleSort('coach')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Coach</Text>{getSortIcon('coach')}</View>
                    </Pressable>
                    <Pressable style={styles.headerCell} onPress={() => handleSort('registrationStatus')}>
                        <View style={styles.headerCellInner}><Text style={styles.headerText}>Action</Text>{getSortIcon('registrationStatus')}</View>
                    </Pressable>
                </View>

                {/* Add Session Row - only when editing */}
                {isEditing && addSession && (
                    <Pressable
                        style={[styles.row, styles.addSessionRow]}
                        onPress={addSession}
                    >
                        <View style={[styles.cellText, styles.addSessionCell]}>
                            <MaterialCommunityIcons name="plus" size={20} color="#34C759" style={{ marginRight: 8 }} />
                            <Text style={styles.addSessionText}>Add New Session</Text>
                        </View>
                        <View style={[styles.dateCellText, styles.dateCell]}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.cellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.cellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.compactCellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.extraCompactCellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.cellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.extraCompactCellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.compactCellText}><Text style={styles.placeholderText}>-</Text></View>
                        <View style={styles.cellText}><Text style={styles.placeholderText}>-</Text></View>
                    </Pressable>
                )}

                {/* Table Rows */}
                {filteredAndSortedSessions.map(([sessionId, session]: [string, any], index) => (
                    <View
                        key={sessionId}
                        style={[
                            styles.row,
                            index % 2 === 0 ? styles.evenRow : styles.oddRow
                        ]}
                    >
                        <View style={styles.cellText}>
                            <View style={styles.titleCellContainer}>
                                {isEditing && (
                                    <Pressable
                                        onPress={() => handleDeleteSession(sessionId)}
                                        onHoverIn={() => setHoveredCell(`${sessionId}-delete`)}
                                        onHoverOut={() => setHoveredCell(null)}
                                        style={[
                                            styles.inlineDeleteButton,
                                            hoveredCell === `${sessionId}-delete` && styles.hoveredInlineDeleteButton
                                        ]}
                                    >
                                        <MaterialCommunityIcons
                                            name="trash-can-outline"
                                            size={14}
                                            color={hoveredCell === `${sessionId}-delete` ? '#dc3545' : '#999'}
                                        />
                                    </Pressable>
                                )}
                                <Pressable
                                    onPress={() => {
                                        if (isEditing) {
                                            setEditingModalSessionId(sessionId);
                                            setModalType('title');
                                        }
                                    }}
                                    onHoverIn={() => {
                                        if (isEditing) {
                                            setHoveredCell(`${sessionId}-title`);
                                        }
                                    }}
                                    onHoverOut={() => {
                                        setHoveredCell(null);
                                    }}
                                    style={[
                                        styles.titlePressable,
                                        isEditing && styles.editableCellPressable,
                                        hoveredCell === `${sessionId}-title` && styles.hoveredCell
                                    ]}
                                >
                                    <Text style={{ textAlign: 'center' }} numberOfLines={2}>
                                        {session.title || 'Untitled Session'}
                                    </Text>
                                    {hoveredCell === `${sessionId}-title` && (
                                        <MaterialCommunityIcons
                                            name="pencil"
                                            size={12}
                                            color="#666"
                                            style={styles.editIcon}
                                        />
                                    )}
                                </Pressable>
                            </View>
                        </View>
                        <View style={[styles.dateCellText, styles.dateCell]}>
                            <Pressable
                                onPress={() => {
                                    if (isEditing) {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('dateRange');
                                    } else {
                                        handleCalendarPress(session);
                                    }
                                }}
                                onHoverIn={() => {
                                    setHoveredCell(`${sessionId}-dateRange`);
                                }}
                                onHoverOut={() => {
                                    setHoveredCell(null);
                                }}
                                style={[
                                    styles.cellPressable,
                                    isEditing && styles.editableCellPressable,
                                    hoveredCell === `${sessionId}-dateRange` && styles.hoveredCell
                                ]}
                            >
                                <Text style={[styles.dateText, { marginBottom: 5 }]}>
                                    {(session.startDate || 'N/A')} - {(session.endDate || 'N/A')} {session.daysOfWeek && session.daysOfWeek.length > 0 && session.daysOfWeek.length < 7 ? SessionUtils.formatDaysAbbrev(session.daysOfWeek, session.daysMask) : ''}
                                </Text>
                                <Text style={[styles.durationText]}>
                                    {calculateDaysDuration(session.startDate, session.endDate)}
                                </Text>
                                {hoveredCell === `${sessionId}-dateRange` && (
                                    <MaterialCommunityIcons
                                        name={isEditing ? "pencil" : "calendar-plus"}
                                        size={12}
                                        color="#666"
                                        style={styles.editIcon}
                                    />
                                )}
                            </Pressable>
                        </View>
                        <View style={styles.cellText}>
                            <Pressable
                                onPress={() => {
                                    if (isEditing) {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('timeRange');
                                    }
                                }}
                                onHoverIn={() => {
                                    if (isEditing) {
                                        setHoveredCell(`${sessionId}-timeRange`);
                                    }
                                }}
                                onHoverOut={() => {
                                    setHoveredCell(null);
                                }}
                                style={[
                                    styles.cellPressable,
                                    isEditing && styles.editableCellPressable,
                                    hoveredCell === `${sessionId}-timeRange` && styles.hoveredCell
                                ]}
                            >
                                <Text style={[styles.dateText, { marginBottom: 5 }]}>
                                    {formatTime(session.startTime)} - {formatTime(session.endTime)}
                                </Text>
                                <Text style={[styles.durationText]}>
                                    {calculateHoursDuration(session.startTime, session.endTime)}
                                </Text>
                                {hoveredCell === `${sessionId}-timeRange` && (
                                    <MaterialCommunityIcons
                                        name="pencil"
                                        size={12}
                                        color="#666"
                                        style={styles.editIcon}
                                    />
                                )}
                            </Pressable>
                        </View>
                        <View style={styles.compactCellText}>
                            <Pressable
                                onPress={() => {
                                    if (isEditing) {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('price');
                                    }
                                }}
                                onHoverIn={() => {
                                    if (isEditing) {
                                        setHoveredCell(`${sessionId}-price`);
                                    }
                                }}
                                onHoverOut={() => {
                                    setHoveredCell(null);
                                }}
                                style={[
                                    styles.cellPressable,
                                    isEditing && styles.editableCellPressable,
                                    hoveredCell === `${sessionId}-price` && styles.hoveredCell
                                ]}
                            >
                                <Text style={{ textAlign: 'center' }}>
                                    {formatPrice(session.price)}
                                </Text>
                                {hoveredCell === `${sessionId}-price` && (
                                    <MaterialCommunityIcons
                                        name="pencil"
                                        size={12}
                                        color="#666"
                                        style={styles.editIcon}
                                    />
                                )}
                            </Pressable>
                        </View>
                        <View style={styles.extraCompactCellText}>
                            <Pressable
                                onPress={() => {
                                    if (isEditing) {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('classSize');
                                    }
                                }}
                                onHoverIn={() => {
                                    if (isEditing) {
                                        setHoveredCell(`${sessionId}-classSize`);
                                    }
                                }}
                                onHoverOut={() => {
                                    setHoveredCell(null);
                                }}
                                style={[
                                    styles.cellPressable,
                                    isEditing && styles.editableCellPressable,
                                    hoveredCell === `${sessionId}-classSize` && styles.hoveredCell
                                ]}
                            >
                                <Text style={{ textAlign: 'center' }}>
                                    {formatCapacity(session.capacity)}
                                </Text>
                                {hoveredCell === `${sessionId}-classSize` && (
                                    <MaterialCommunityIcons
                                        name="pencil"
                                        size={12}
                                        color="#666"
                                        style={styles.editIcon}
                                    />
                                )}
                            </Pressable>
                        </View>
                        <View style={styles.compactCellText}>
                            <Pressable
                                onPress={() => {
                                    if (isEditing) {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('ageRange');
                                    }
                                }}
                                onHoverIn={() => {
                                    if (isEditing) {
                                        setHoveredCell(`${sessionId}-ageRange`);
                                    }
                                }}
                                onHoverOut={() => {
                                    setHoveredCell(null);
                                }}
                                style={[
                                    styles.cellPressable,
                                    isEditing && styles.editableCellPressable,
                                    hoveredCell === `${sessionId}-ageRange` && styles.hoveredCell
                                ]}
                            >
                                <Text style={{ textAlign: 'center' }}>
                                    {formatAgeRange(session.startAge, session.endAge)}
                                </Text>
                                {hoveredCell === `${sessionId}-ageRange` && (
                                    <MaterialCommunityIcons
                                        name="pencil"
                                        size={12}
                                        color="#666"
                                        style={styles.editIcon}
                                    />
                                )}
                            </Pressable>
                        </View>

                        {/* Coach */}
                        <View style={styles.cellText}>
                            {isEditing ? (
                                <Pressable
                                    onPress={() => {
                                        setEditingModalSessionId(sessionId);
                                        setModalType('instructor');
                                    }}
                                    onHoverIn={() => setHoveredCell(`${sessionId}-instructor`)}
                                    onHoverOut={() => setHoveredCell(null)}
                                    style={[
                                        styles.cellPressable,
                                        isEditing && styles.editableCellPressable,
                                        hoveredCell === `${sessionId}-instructor` && styles.hoveredCell
                                    ]}
                                >
                                    <Text style={{ textAlign: 'center' }}>
                                        {session.coach ? `${session.coach.firstName || ''} ${session.coach.lastName || ''}`.trim() || 'Coach' : 'No coach'}
                                    </Text>
                                    {hoveredCell === `${sessionId}-instructor` && (
                                        <MaterialCommunityIcons
                                            name="pencil"
                                            size={12}
                                            color="#666"
                                            style={styles.editIcon}
                                        />
                                    )}
                                </Pressable>
                            ) : (
                                <Pressable
                                    onPress={() => handleCoachEmailPress(session)}
                                    onHoverIn={() => setHoveredCell(`${sessionId}-instructor`)}
                                    onHoverOut={() => setHoveredCell(null)}
                                    style={[
                                        styles.cellPressable,
                                        hoveredCell === `${sessionId}-instructor` && styles.hoveredCell
                                    ]}
                                    disabled={!session.coach?.email}
                                >
                                    <Text style={{
                                        textAlign: 'center',
                                        color: session.coach?.email ?
                                            (hoveredCell === `${sessionId}-instructor` ? '#2C67D6' : '#333') :
                                            '#666'
                                    }}>
                                        {session.coach ? `${session.coach.firstName || ''} ${session.coach.lastName || ''}`.trim() || 'Coach' : 'No coach'}
                                    </Text>
                                </Pressable>
                            )}
                        </View>

                        {/* Removed Meals and Cancellation columns */}

                        <View style={styles.cellText}>
                            <View style={styles.tableRegistrationBannerWrapper}>
                                <RegistrationBanner
                                    sessionId={session.sessionId}
                                    session={session}
                                    isHost={isHost}
                                    isEditing={isEditing}
                                    registeredUser={isUserRegistered(session.sessionId)}
                                    updateValue={() => { }}
                                    initialView={sessionIdFromUrl === session.sessionId ? urlViewType : null}
                                    initialDate={sessionIdFromUrl === session.sessionId ? urlDate : null}
                                />
                            </View>
                        </View>
                    </View>
                ))}
            </View>

            {/* Edit Modals */}
            <ClassSizeEditModal
                visible={editingModalSessionId !== null && modalType === 'classSize'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentCapacity={editingModalSessionId ? sessions[editingModalSessionId]?.capacity : ''}
                onSave={(newCapacity) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                capacity: newCapacity
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Class Size'}
            />

            <PriceEditModal
                visible={editingModalSessionId !== null && modalType === 'price'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentPrice={editingModalSessionId ? sessions[editingModalSessionId]?.price : ''}
                onSave={(newPrice) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                price: newPrice
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Price'}
            />

            <TitleEditModal
                visible={editingModalSessionId !== null && modalType === 'title'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title : ''}
                onSave={(newTitle) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                title: newTitle
                            }
                        }));
                    }
                }}
            />

            <AgeRangeEditModal
                visible={editingModalSessionId !== null && modalType === 'ageRange'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentStartAge={editingModalSessionId ? sessions[editingModalSessionId]?.startAge : ''}
                currentEndAge={editingModalSessionId ? sessions[editingModalSessionId]?.endAge : ''}
                onSave={(startAge, endAge) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                startAge: startAge,
                                endAge: endAge
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Age Range'}
            />

            <TimeRangeEditModal
                visible={editingModalSessionId !== null && modalType === 'timeRange'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentStartTime={editingModalSessionId ? sessions[editingModalSessionId]?.startTime : ''}
                currentEndTime={editingModalSessionId ? sessions[editingModalSessionId]?.endTime : ''}
                onSave={(startTime, endTime) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                startTime: startTime,
                                endTime: endTime
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Time Range'}
            />

            <DateRangeEditModal
                visible={editingModalSessionId !== null && modalType === 'dateRange'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentStartDate={editingModalSessionId ? sessions[editingModalSessionId]?.startDate : ''}
                currentEndDate={editingModalSessionId ? sessions[editingModalSessionId]?.endDate : ''}
                currentDaysOfWeek={editingModalSessionId ? sessions[editingModalSessionId]?.daysOfWeek : undefined}
                onSave={(startDate, endDate, daysOfWeek) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                startDate: startDate,
                                endDate: endDate,
                                officialStartDate: TimeOps.getOfficialStartDate(startDate) || prev[editingModalSessionId]?.officialStartDate || '',
                                daysOfWeek: daysOfWeek,
                                daysMask: SessionUtils.daysOfWeekToMask(daysOfWeek)
                            }
                        }));
                        // Derive midnight timestamps for querying
                        try {
                            const s = new Date(startDate);
                            const e = new Date(endDate);
                            if (!isNaN(s.getTime()) || !isNaN(e.getTime())) {
                                setSessions(prev => ({
                                    ...prev,
                                    [editingModalSessionId]: {
                                        ...prev[editingModalSessionId],
                                        startAt: (!isNaN(s.getTime()) ? new Date(new Date(s).setHours(0, 0, 0, 0)) : prev[editingModalSessionId]?.startAt),
                                        endAt: (!isNaN(e.getTime()) ? new Date(new Date(e).setHours(23, 59, 59, 999)) : prev[editingModalSessionId]?.endAt),
                                    }
                                }));
                            }
                        } catch { }
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Date Range'}
            />

            <SessionTypeEditModal
                visible={editingModalSessionId !== null && modalType === 'sessionType'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentType={editingModalSessionId ? sessions[editingModalSessionId]?.sessionType : ''}
                onSave={(newType) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                sessionType: newType,
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Session Type'}
            />

            <CoachEditModal
                visible={editingModalSessionId !== null && modalType === 'instructor'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentCoach={editingModalSessionId ? sessions[editingModalSessionId]?.coach : null}
                onSave={async (newCoach) => {
                    if (editingModalSessionId) {
                        const currentSession = sessions[editingModalSessionId];
                        const previousCoach = currentSession?.coach;

                        const coachId = newCoach ? (newCoach.userId || newCoach.id) : null;
                        const coachName = newCoach ? `${newCoach.firstName || ''} ${newCoach.lastName || ''}`.trim() || 'Coach' : null;
                        const minimalCoach = newCoach ? {
                            userId: coachId,
                            name: coachName,
                            email: newCoach.email || null,
                            phone: newCoach.phone || null,
                            assignedAt: new Date(),
                        } : null;

                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                coach: minimalCoach
                            }
                        }));

                        // Send notification to newly assigned coach
                        if (minimalCoach && (!previousCoach || previousCoach.userId !== minimalCoach.userId)) {
                            // Track assignment in coach document
                            try {
                                if (camp?.campId) {
                                    await DataOps.addSessionAssignmentToCoach(minimalCoach.userId, camp.campId, editingModalSessionId);
                                }
                            } catch (e) { }
                            try {
                                await NotificationOps.createNotificationForAnyAccount(
                                    minimalCoach.userId,
                                    `You have been assigned as coach for session "${currentSession?.title || 'Untitled Session'}" at ${camp?.campName || 'a camp'}.`,
                                    'session_assigned',
                                    editingModalSessionId,
                                    camp?.campId,
                                    {
                                        sessionTitle: currentSession?.title,
                                        campName: camp?.campName,
                                        startDate: currentSession?.startDate,
                                        endDate: currentSession?.endDate
                                    }
                                );
                            } catch (error) {
                                console.error('Error sending notification to coach:', error);
                            }
                        }

                        // Send notification to previously assigned coach if they were removed/replaced
                        if (previousCoach && (!minimalCoach || previousCoach.userId !== minimalCoach.userId)) {
                            // Remove assignment from previous coach document
                            try {
                                if (camp?.campId) {
                                    await DataOps.removeSessionAssignmentFromCoach(previousCoach.userId, camp.campId, editingModalSessionId);
                                }
                            } catch (e) { }
                            try {
                                await NotificationOps.createNotificationForAnyAccount(
                                    previousCoach.userId,
                                    `You have been unassigned from session "${currentSession?.title || 'Untitled Session'}" at ${camp?.campName || 'a camp'}.`,
                                    'session_unassigned',
                                    editingModalSessionId,
                                    camp?.campId,
                                    {
                                        sessionTitle: currentSession?.title,
                                        campName: camp?.campName,
                                        startDate: currentSession?.startDate,
                                        endDate: currentSession?.endDate
                                    }
                                );
                            } catch (error) {
                                console.error('Error sending unassignment notification to previous coach:', error);
                            }
                        }
                    }
                }}
                campCoaches={coachData}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Set Coach'}
            />

            <CancellationPolicyEditModal
                visible={editingModalSessionId !== null && modalType === 'cancellation'}
                onClose={() => {
                    setEditingModalSessionId(null);
                    setModalType(null);
                }}
                currentPolicy={editingModalSessionId ? sessions[editingModalSessionId]?.cancellationPolicy : ''}
                onSave={(newPolicy) => {
                    if (editingModalSessionId) {
                        setSessions(prev => ({
                            ...prev,
                            [editingModalSessionId]: {
                                ...prev[editingModalSessionId],
                                cancellationPolicy: newPolicy
                            }
                        }));
                    }
                }}
                sessionTitle={editingModalSessionId ? sessions[editingModalSessionId]?.title || 'Untitled Session' : 'Edit Cancellation Policy'}
            />

            {/* Coach View Modal for notification navigation */}
            <CoachViewModal
                visible={showCoachViewModal}
                onClose={() => {
                    setShowCoachViewModal(false);
                    setCoachViewSessionId(null);
                    setCoachViewCampId(null);
                }}
                sessionId={coachViewSessionId}
                campId={coachViewCampId}
                currentUser={user}
                isCoach={isHost} // Use isHost which now includes coaches
                initialView={urlViewType}
                initialDate={urlDate}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: 'white',
        borderRadius: 12,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
        width: '100%',
        flex: 1,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#f8f9fa',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    searchIcon: {
        marginRight: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: '#333',
    },
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#f8f9fa',
        paddingVertical: 12,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    tableCard: {
        backgroundColor: 'white',
        borderRadius: 12,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
        elevation: 3,
        borderWidth: 1,
        borderColor: '#eef0f3',
    },
    headerCell: {
        flex: 1,
        alignItems: 'center',
    },
    dateHeaderCell: {
        flex: 1.5,
        alignItems: 'center',
    },
    compactHeaderCell: {
        flex: 0.6,
        alignItems: 'center',
    },
    extraCompactHeaderCell: {
        flex: 0.5,
        alignItems: 'center',
    },
    headerText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#3C8248',
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    headerCellInner: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    row: {
        flexDirection: 'row',
        paddingVertical: 14,
        paddingHorizontal: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#f2f4f7',
        alignItems: 'center',
        minHeight: 60,
    },
    evenRow: {
        backgroundColor: '#ffffff',
    },
    oddRow: {
        backgroundColor: '#fbfcfe',
    },
    cellText: {
        flex: 1,
        fontSize: 13,
        color: '#333',
        textAlign: 'center',
        lineHeight: 18,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 40,
    },
    dateCellText: {
        flex: 1.5,
        fontSize: 13,
        color: '#333',
        textAlign: 'center',
        lineHeight: 18,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 40,
    },
    compactCellText: {
        flex: 0.6,
        fontSize: 13,
        color: '#333',
        textAlign: 'center',
        lineHeight: 18,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 40,
    },
    extraCompactCellText: {
        flex: 0.5,
        fontSize: 13,
        color: '#333',
        textAlign: 'center',
        lineHeight: 18,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 40,
    },
    actionButton: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 80,
    },
    actionButtonText: {
        fontSize: 12,
        fontWeight: '500',
        color: 'white',
    },
    registerButton: {
        backgroundColor: '#2C67D6',
    },
    waitlistButton: {
        backgroundColor: '#FF6B35',
    },
    unregisterButton: {
        backgroundColor: '#DC3545',
    },
    fullButton: {
        backgroundColor: '#6C757D',
    },
    statusText: {
        fontSize: 12,
        color: '#666',
        textAlign: 'center',
    },
    tableBannerWrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        height: 24,
        transform: [{ scale: 0.8 }],
        paddingTop: 8,
    },
    tableRegistrationBannerWrapper: {
        alignItems: 'center',
        justifyContent: 'center',
        height: 24,
        transform: [{ scale: 0.8 }],
        paddingTop: 8,
    },
    campTypeContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dateCell: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    dateText: {
        fontSize: 13,
        color: '#333',
        textAlign: 'center',
        lineHeight: 18,
    },
    durationText: {
        fontSize: 10,
        color: 'grey',
        textAlign: 'center',
        lineHeight: 14,
    },
    addSessionRow: {
        backgroundColor: '#f8fff8',
        borderLeftWidth: 3,
        borderLeftColor: '#34C759',
    },
    addSessionCell: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    addSessionText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#34C759',
        textAlign: 'center',
    },
    placeholderText: {
        fontSize: 13,
        color: '#ccc',
        textAlign: 'center',
    },
    cellPressable: {
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 4,
        position: 'relative',
    },
    editableCellPressable: {
        borderRadius: 4,
    },
    hoveredCell: {
        backgroundColor: '#e3f2fd',
        borderWidth: 1,
        borderColor: '#2196f3',
        borderRadius: 4,
    },
    editIcon: {
        position: 'absolute',
        top: 2,
        right: 2,
        opacity: 0.7,
    },
    mealsToggle: {
        paddingHorizontal: 4,
        paddingVertical: 4,
        borderRadius: 4,
        marginLeft: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hoveredMealsToggle: {
        backgroundColor: '#f0f8ff',
        borderWidth: 1,
        borderColor: '#e3f2fd',
    },
    titleCellContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    titlePressable: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 4,
        position: 'relative',
    },
    inlineDeleteButton: {
        padding: 6,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 8,
    },
    hoveredInlineDeleteButton: {
        backgroundColor: '#ffeaea',
        borderWidth: 1,
        borderColor: '#dc3545',
    },
});

export default SessionsTableView; 